/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import SearchBar from '../containers/SearchBar/SearchBar';
import SearchProvider from '../providers/SearchProvider';
import SearchHostContext from '../utils/SearchHostContext';
import { getLogger } from '../utils/SearchLogger';
import SearchUtils from '../utils/SearchUtils';
import ReactDOM from 'react-dom';

export type SearchBarViewProps = {
	searchHostContext: HostContext;
	onSubmit?: (event: React.FormEvent) => void;
	initialValue?: string;
	maxLength?: string;
	placeholder?: string;
	theme?: string;
	// TODO: Check why ccd is still passing it
	hostShowSearchDropdown?: boolean;
	leftNavOn?: boolean;
	muted?: boolean;
	enableSearchOptimisationFG?: boolean;
};

class SearchBarView extends Component<SearchBarViewProps> {
	logger: Logger;
	isSearchDisabled: boolean;
	static appRef: React.RefObject<SearchBarView>;

	constructor(props: SearchBarViewProps) {
		super(props);
		this.isSearchDisabled = true;
		if (!props.searchHostContext) {
			throw new Error('A HostContext property must be passed to SearchBar');
		}

		SearchHostContext.setContext(props.searchHostContext);
		// create a logger for all components to use
		this.logger = getLogger();
	}

	/*
	 ** Called from Host apps to exit search
	 */
	onExit(): void {
		const customEvent = new CustomEvent('onSearchExit');
		window.dispatchEvent(customEvent);
	}

	// Method needs to be invoked by client on takeover
	initiateSearch() {
		SearchUtils.initializeSession();
	}

	/**
	 * There is an issue, I failed to figure out how not to pass props from here to SearchBar
	 * The props are actually passed down from search provider
	 * I have made the props optional just to satisfy "tsc"
	 * This needs to be resolved
	 */
	render() {
		if (
			this.props.muted &&
			this.isSearchDisabled &&
			this.props.searchHostContext.hostInfo.surface === 'CCDesktop' &&
			this.props.enableSearchOptimisationFG
		) {
			return false;
		}
		this.isSearchDisabled = false;
		return (
			<SearchProvider logger={this.logger} {...this.props} showErrorUI={false}>
				<SearchBar searchHostContext={this.props.searchHostContext} />
			</SearchProvider>
		);
	}

	static renderSearchBar({ rootNode, props }: { rootNode: HTMLElement; props: SearchBarViewProps }): SearchBarView {
		this.appRef = React.createRef<SearchBarView>();
		ReactDOM.render(<SearchBarView ref={SearchBarView.appRef} {...props} />, rootNode);
		return this.appRef.current;
	}
}
export default SearchBarView;
